import React, { useEffect } from 'react';
import { X, XCircle } from 'lucide-react';
import checkCircleIcon from '../../assets/icons/check_circle.svg';

interface ToastProps {
  message: string;
  onClose: () => void;
  duration?: number;
  className?: string;
  variant?: 'success' | 'error';
  /** Position the toast fixed on screen (default: true for popup behavior) */
  fixed?: boolean;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  onClose,
  duration = 5000,
  className = '',
  variant = 'success',
  fixed = true,
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const variantStyles = {
    success: {
      container: 'bg-white border-gray-200',
      text: 'text-gray-900',
      icon: checkCircleIcon,
      iconColor: 'text-green-500',
      useImage: true,
    },
    error: {
      container: 'bg-red-50 border-red-200',
      text: 'text-red-900',
      icon: XCircle,
      iconColor: 'text-red-900',
      useImage: false,
    },
  };

  const style = variantStyles[variant];

  // Use very high z-index (9999) to ensure toast appears above header and all other elements
  const fixedClasses = fixed
    ? 'fixed top-20 right-4 max-w-md'
    : 'relative w-full max-w-full mb-4';

  return (
    <div
      className={`animate-slide-up ${style.container} rounded-lg shadow-lg border py-2 px-6 ${fixedClasses} ${className}`}
      style={fixed ? { zIndex: 9999 } : undefined}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="flex items-center justify-center w-8 h-8">
            {style.useImage ? (
              <img
                src={style.icon as string}
                alt={variant === 'success' ? 'Success' : 'Error'}
                className="w-6 h-6 mx-2"
              />
            ) : (
              React.createElement(style.icon as React.ComponentType<any>, {
                className: `w-6 h-6 ${style.iconColor}`,
              })
            )}
          </div>

          <p className={`text-sm font-medium ${style.text} leading-5 m-0`}>
            {message}
          </p>
        </div>

        <button
          onClick={onClose}
          onMouseDown={(e) => e.preventDefault()}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center w-8 h-8 focus:outline-none focus:ring-0"
          aria-label="Close notification"
          style={{
            background: 'none',
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            padding: '4px',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
